'use client';

import { Dialog, DialogContent, DialogHeader, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { IconChevronLeft } from '@tabler/icons-react';
import { useTranslations } from 'next-intl';
import React from 'react';
import { TelegramAvailableFeaturesFeatureTopUpDto } from '@/generated';
import clsx from 'clsx';
import Image from 'next/image';
import { getFeaturePriceBreakdown } from '@/utils/feature-price';
import { CurrencySign } from '../../common/CurrencySign';

type Props = {
  onClose: () => void;
  onSelect: (id: string) => void;
  available: TelegramAvailableFeaturesFeatureTopUpDto;
  onGoBack: () => void;
  selected: string[];
  onGoNext: () => void;
  disabledNext?: boolean;
};

export const TopUp: React.FC<Props> = ({
  onClose,
  onSelect,
  available,
  selected,
  onGoNext,
  onGoBack,
  disabledNext = false,
}) => {
  const t = useTranslations('monetization');

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='sm:max-w-xl'>
        <Button
          variant='ghost'
          size='icon'
          onClick={onGoBack}
          className='absolute top-4 start-4 z-10 h-8 w-8 rounded-full hover:bg-transparent'
        >
          <IconChevronLeft className='text-text-primary' />
        </Button>
        <div className='flex flex-col gap-[16px]'>
          <DialogHeader className='items-center text-center'>
            <DialogTitle>{available.header}</DialogTitle>
            <DialogDescription>{available.text}</DialogDescription>
          </DialogHeader>

          <div className='flex flex-col gap-[16px]'>
            {available.values.map((value) => {
              const { icon, daysAmountText, daysAmount, cardHeader, cardText, viewMultiply, discount, footerText, id } =
                value;
              const price = getFeaturePriceBreakdown(value);

              return (
                <div
                  key={id}
                  onClick={() => {
                    onSelect(id);
                  }}
                  className={clsx(
                    'relative py-[14px] px-[12px] flex flex-col gap-[8px] bg-surface-light rounded-[12px] cursor-pointer',
                    {
                      'outline-2 outline-double outline-main-green': selected.includes(id),
                    },
                  )}
                >
                  {!!discount && discount > 0 && (
                    <div
                      className={clsx(
                        'absolute left-[14px] top-[-10px] z-1 bg-[#FF4D00] rounded-[4px] px-[4px] py-px flex justify-center items-center text-[#FFF] text-[11px]',
                      )}
                    >
                      -{discount}%
                    </div>
                  )}
                  <div className='flex gap-[24px]'>
                    <div className='rounded-[12px] flex items-center justify-center w-[24px] overflow-hidden'>
                      <Image src={icon} width={24} height={24} alt='' />
                    </div>

                    <div className='flex flex-col'>
                      <div className='leading-[18px] text-[14px] text-text-disabled-day'>{t('price')}</div>
                      <div className='flex gap-[4px]'>
                        <div className='text-[18px] leading-[21px] text-black font-medium'>
                          {price.fiatAmount} <CurrencySign value={price.currencySign} />
                        </div>
                        <div
                          className={clsx('text-[18px] leading-[21px] text-black opacity-40 line-through', {
                            'opacity-0!': !price.fiatFakeAmount || price.fiatFakeAmount === price.fiatAmount,
                          })}
                        >
                          {price.fiatFakeAmount}
                        </div>
                      </div>
                    </div>

                    <div className='flex flex-col ms-[4px]'>
                      <div className='leading-[18px] text-[14px] text-text-disabled-day'>{t('time')}</div>
                      <div className='flex'>
                        <div className='text-[18px] leading-[21px] text-black font-medium'>
                          {daysAmountText ? daysAmountText : daysAmount}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className='text-black text-[18px] font-medium leading-[21px]'>{cardHeader}</div>
                    <div className='mt-[4px] text-[16px] leading-[20px] text-primary'>{cardText}</div>
                  </div>

                  <div className='flex justify-between border-t border-solid pt-[8px] border-primary-stroke-day'>
                    <div className='text-[16px] leading-[20px] text-text-disabled-day'>{footerText}</div>
                    <div className='text-[16px] leading-[20px] text-primary'>
                      {t('view_boost_value', { value: viewMultiply })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <Button disabled={disabledNext} onClick={onGoNext} className='w-full h-auto py-[10px]'>
            {t('continue')}
          </Button>

          {/* </div> */}
        </div>
      </DialogContent>
    </Dialog>
  );
};
